import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { API_BASE_URL } from '../../api/client';

export default function ManualInvoiceForm({ recordId, onSuccess, onCancel }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({
        numero_facture: '',
        date_facture: '',
        reparateur_nom: '',
        matricule_fiscale: '',
        numero_devis: '',
        adresse: '',
        taux_tva_default: '19',
        commentaire: 'RAS' // Default value as requested? Or empty
    });

    const [customTva, setCustomTva] = useState('');
    const [isCustomTva, setIsCustomTva] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleTvaChange = (e) => {
        const value = e.target.value;
        if (value === 'custom') {
            setIsCustomTva(true);
            setFormData(prev => ({ ...prev, taux_tva_default: '' }));
        } else {
            setIsCustomTva(false);
            setFormData(prev => ({ ...prev, taux_tva_default: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                record_id: recordId,
                ...formData,
                taux_tva_default: isCustomTva ? customTva : formData.taux_tva_default
            };

            const response = await fetch(`${API_BASE_URL}/manual-invoices`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.detail || "Erreur lors de la création de la facture");
            }

            const newInvoice = await response.json();
            onSuccess(newInvoice);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold text-gray-800">Ajouter une Facture Manuelle</h4>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-gray-400 hover:text-gray-600"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>

            {error && (
                <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg mb-4">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Numéro Facture * */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Numéro de facture <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="numero_facture"
                        required
                        value={formData.numero_facture}
                        onChange={handleChange}
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Ex: F-2024-001"
                    />
                </div>

                {/* Date Facture * */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Date facture <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        name="date_facture"
                        required
                        value={formData.date_facture}
                        onChange={handleChange}
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>

                {/* Nom du réparateur / garage * */}
                <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Nom du réparateur / garage <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="reparateur_nom"
                        required
                        value={formData.reparateur_nom}
                        onChange={handleChange}
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Ex: Garage Central"
                    />
                </div>

                {/* Matricule fiscale */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Matricule fiscale
                    </label>
                    <input
                        type="text"
                        name="matricule_fiscale"
                        value={formData.matricule_fiscale}
                        onChange={handleChange}
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>

                {/* Numéro devis */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Numéro devis
                    </label>
                    <input
                        type="text"
                        name="numero_devis"
                        value={formData.numero_devis}
                        onChange={handleChange}
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>

                {/* Adresse */}
                <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Adresse
                    </label>
                    <input
                        type="text"
                        name="adresse"
                        value={formData.adresse}
                        onChange={handleChange}
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                </div>

                {/* Taux TVA par défaut * */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Taux TVA par défaut <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2">
                        <select
                            name="taux_tva_default"
                            required={!isCustomTva}
                            value={isCustomTva ? 'custom' : formData.taux_tva_default}
                            onChange={handleTvaChange}
                            className="flex-1 text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        >
                            <option value="7">7%</option>
                            <option value="13">13%</option>
                            <option value="19">19%</option>
                            <option value="custom">Autre...</option>
                        </select>
                        {isCustomTva && (
                            <input
                                type="number"
                                placeholder="%"
                                required
                                value={customTva}
                                onChange={(e) => setCustomTva(e.target.value)}
                                className="w-20 text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            />
                        )}
                    </div>
                </div>

                {/* Commentaire général */}
                <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Commentaire général
                    </label>
                    <textarea
                        name="commentaire"
                        rows="2"
                        value={formData.commentaire}
                        onChange={handleChange}
                        className="w-full text-sm border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    ></textarea>
                </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Annuler
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-70"
                >
                    {loading ? (
                        <>Sauvegarde...</>
                    ) : (
                        <>
                            <Check className="w-4 h-4" />
                            Ajouter la facture
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}
